import {keysFirstLetterToLowerCase} from './callSessionUtils';
import {normalizeHubMonitoringRows, passesAllFilters} from './monitoringHubMerge';

type MonitoringFilter = {key: string; value: any};

type DashboardBucket = 'active' | 'success' | 'unsuccess';

type CallContribution = {group: string; bucket: DashboardBucket};

export type MonitoringDashboardTracker = {
    boundaryMs: number;
    contributions: Map<string, CallContribution | null>;
    snapshotActive: Map<string, number>;
    members: Map<string, string[]>;
    ignored: Set<string>;
};

const TERMINAL_BUCKETS: Record<string, DashboardBucket> = {
    '103': 'success',
    success: 'success',
    '104': 'unsuccess',
    unsuccess: 'unsuccess'
};

const CALL_GROUP_RULES: Array<{group: string; directions: string[]; callTypes: string[]}> = [
    {group: 'CampaignExternal', directions: ['campaign'], callTypes: ['external']},
    {group: 'CampaignInternal', directions: ['campaign'], callTypes: ['internal']},
    {group: 'CampaignIvm', directions: ['campaign'], callTypes: ['ivm']},
    {group: 'InternalQueue', directions: ['inbound', 'queue'], callTypes: ['internal']},
    {group: 'InternalQueue', directions: ['queue'], callTypes: ['ivr']},
    {group: 'InternalPickupCall', directions: ['inbound', 'queue'], callTypes: ['pickupcall']},
    {group: 'InternalCallBack', directions: ['p2p', 'queue'], callTypes: ['callbackinternal']},
    {group: 'InternalConsultation', directions: ['p2p', 'queue'], callTypes: ['consultationinternal']},
    {group: 'InternalTransfer', directions: ['p2p', 'queue'], callTypes: ['transferinternal']},
    {group: 'InternalIvr', directions: ['inbound'], callTypes: ['ivr']},
    {group: 'InternalP2P', directions: ['p2p'], callTypes: ['internal']},
    {group: 'ExternalCallBack', directions: ['outbound', 'p2p'], callTypes: ['callbackexternal']},
    {group: 'ExternalConsultation', directions: ['outbound', 'queue', 'p2p'], callTypes: ['consultationexternal']},
    {group: 'ExternalOutbound', directions: ['outbound'], callTypes: ['external']},
    {group: 'ExternalTransfer', directions: ['outbound'], callTypes: ['transferexternal']},
    {group: 'ExternalC2C', directions: ['c2c'], callTypes: ['external']},
    {group: 'ExternalP2P', directions: ['p2p'], callTypes: ['external']},
    {group: 'ExternalSpyCall', directions: ['outbound'], callTypes: ['spycall']},
    {group: 'ExternalQueue', directions: ['queue'], callTypes: ['external']}
];

const DASHBOARD_BLOCKS = [
    {prefix: 'Campaign', itemsKey: 'campaigns', totalKey: 'campaignsTotal'},
    {prefix: 'Internal', itemsKey: 'internal', totalKey: 'internalTotal'},
    {prefix: 'External', itemsKey: 'external', totalKey: 'externalTotal'}
];

export function getCallGroup(item: any): string | null {
    const direction = String(item?.direction ?? '').trim().toLowerCase();
    const callType = String(item?.callType ?? '').trim().toLowerCase();

    if (!direction || !callType) {
        return null;
    }

    const rule = CALL_GROUP_RULES.find(
        candidate => candidate.directions.includes(direction) && candidate.callTypes.includes(callType)
    );

    return rule ? rule.group : null;
}

export function createMonitoringDashboardTracker(boundaryMs: number, dashboard?: any): MonitoringDashboardTracker {
    const snapshotActive = new Map<string, number>();

    DASHBOARD_BLOCKS.forEach(block => {
        const items = Array.isArray(dashboard?.[block.itemsKey]) ? dashboard[block.itemsKey] : [];

        items.forEach((row: any) => {
            const group = String(row?.callGroup ?? '').trim().toLowerCase();

            if (group) {
                snapshotActive.set(group, Number(row?.active ?? 0));
            }
        });
    });

    return {
        boundaryMs: Number.isFinite(boundaryMs) ? boundaryMs : Date.now(),
        contributions: new Map(),
        snapshotActive,
        members: new Map(),
        ignored: new Set()
    };
}

function getRootKey(root: any): string | null {
    const raw = root?.item?.groupSessionId ?? root?.item?.id;

    return raw === null || raw === undefined || raw === '' ? null : String(raw);
}

function toTimeMs(value: any): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const raw = String(value);
    const parsed = Date.parse(raw);

    if (!Number.isNaN(parsed)) {
        return parsed;
    }

    // Hub timestamps sometimes have 7-digit fractions, which Date.parse rejects.
    const fallback = Date.parse(raw.replace(/(\.\d{3})\d+/, '$1'));
    return Number.isNaN(fallback) ? null : fallback;
}

function getDashboardHubFilters(filters: MonitoringFilter[] | undefined): MonitoringFilter[] | undefined {
    if (!filters?.length) {
        return filters;
    }

    // Dashboard buckets ARE the status split. A list filter like Status=101 would
    // let a call enter active and then hide the 103/104 that should take it out.
    return filters.filter(filter => String(filter?.key) !== 'Status');
}

function createEmptyDashboardItem(callGroup: string) {
    return {callGroup, total: 0, active: 0, success: 0, unsuccess: 0};
}

function cloneDashboardItem(item: any, callGroup: string) {
    return item ? {...item} : createEmptyDashboardItem(callGroup);
}

function cloneDashboard(dashboard: any) {
    const next: any = {...dashboard};

    DASHBOARD_BLOCKS.forEach(block => {
        const items = Array.isArray(dashboard?.[block.itemsKey]) ? dashboard[block.itemsKey] : [];
        next[block.itemsKey] = items.map((item: any) => ({...item}));
        next[block.totalKey] = cloneDashboardItem(dashboard?.[block.totalKey], '');
    });

    next.total = cloneDashboardItem(dashboard?.total, '');

    return next;
}

function findBlock(group: string) {
    return DASHBOARD_BLOCKS.find(block => group.startsWith(block.prefix)) || null;
}

function ensureRow(draft: any, itemsKey: string, group: string) {
    const items: any[] = Array.isArray(draft[itemsKey]) ? draft[itemsKey] : [];
    draft[itemsKey] = items;

    const existing = items.find(
        item => String(item?.callGroup ?? '').toLowerCase() === group.toLowerCase()
    );

    if (existing) {
        return existing;
    }

    const created = createEmptyDashboardItem(group);
    items.push(created);
    return created;
}

function bumpMetric(item: any, metric: string, delta: number) {
    const next = Number(item[metric] ?? 0) + delta;
    item[metric] = next > 0 ? next : 0;
}

function sumDashboardMetrics(items: any[]) {
    return (items || []).reduce(
        (acc, item) => {
            acc.total += Number(item?.total ?? 0);
            acc.active += Number(item?.active ?? 0);
            acc.success += Number(item?.success ?? 0);
            acc.unsuccess += Number(item?.unsuccess ?? 0);
            return acc;
        },
        {total: 0, active: 0, success: 0, unsuccess: 0}
    );
}

function reconcileDashboardTotals(draft: any) {
    const grand = {total: 0, active: 0, success: 0, unsuccess: 0};

    DASHBOARD_BLOCKS.forEach(block => {
        const items = Array.isArray(draft?.[block.itemsKey]) ? draft[block.itemsKey] : [];
        const summed = sumDashboardMetrics(items);

        if (draft[block.totalKey]) {
            draft[block.totalKey] = {...draft[block.totalKey], ...summed};
        }

        grand.total += summed.total;
        grand.active += summed.active;
        grand.success += summed.success;
        grand.unsuccess += summed.unsuccess;
    });

    if (draft?.total) {
        draft.total = {...draft.total, ...grand};
    }
}

function applyContribution(draft: any, contribution: CallContribution, sign: number) {
    const block = findBlock(contribution.group);

    if (!block) {
        return;
    }

    const targets = [
        ensureRow(draft, block.itemsKey, contribution.group),
        draft[block.totalKey],
        draft.total
    ];

    targets.forEach(target => {
        if (!target) {
            return;
        }
        bumpMetric(target, 'total', sign);
        bumpMetric(target, contribution.bucket, sign);
    });
}

function sameContribution(left: CallContribution | null, right: CallContribution | null): boolean {
    if (!left || !right) {
        return left === right;
    }

    return left.group === right.group && left.bucket === right.bucket;
}

function flattenHubRows(rows: any[]): any[] {
    const wrappers: any[] = [];

    const walk = (list: any[]) => {
        (list || []).forEach(row => {
            if (row?.item) {
                wrappers.push({item: row.item});
            }

            if (Array.isArray(row?.children) && row.children.length) {
                walk(row.children);
            }
        });
    };

    walk(rows);

    return wrappers;
}

function claimSnapshotContribution(
    tracker: MonitoringDashboardTracker,
    next: CallContribution | null,
    startedAfterSnapshot: boolean,
    endedBeforeSnapshot: boolean
): CallContribution | null {
    if (!next || startedAfterSnapshot) {
        return null;
    }

    if (endedBeforeSnapshot) {
        return {group: next.group, bucket: next.bucket};
    }

    const claimKey = next.group.toLowerCase();
    const claimable = tracker.snapshotActive.get(claimKey) ?? 0;

    if (claimable < 1) {
        return null;
    }

    tracker.snapshotActive.set(claimKey, claimable - 1);
    return {group: next.group, bucket: 'active'};
}

// Campaign and C2C events often keep Status=101 after End is already set.
// The dashboard invariant is total = active + success + unsuccess, so a call
// with End is no longer in flight even if the status has not flipped yet.
function resolveBucket(status: any, end?: any): DashboardBucket | null {
    const key = String(status ?? '').trim().toLowerCase();
    const ended = toTimeMs(end) !== null;

    if (TERMINAL_BUCKETS[key]) {
        return TERMINAL_BUCKETS[key];
    }

    if (ended) {
        return 'unsuccess';
    }

    if (!key) {
        return null;
    }

    return 'active';
}

export function applyMonitoringDashboardHubBatches(
    dashboard: any,
    batches: any[][],
    tracker: MonitoringDashboardTracker,
    filters: MonitoringFilter[] | undefined
): any {
    if (!dashboard || !tracker || !batches?.length) {
        return dashboard;
    }

    const hubFilters = getDashboardHubFilters(filters);
    let draft: any = null;
    const ensureDraft = () => {
        draft = draft || cloneDashboard(dashboard);
        return draft;
    };
    const seenKeys = new Set<string>();
    const seenRoots = new Set<string>();

    // The hub re-sends a call with the legs it still has, so a leg that is gone from
    // the payload has to give its contribution back the way the list drops such rows.
    const releaseMissingLegs = (root: any, seenKeys: string[]) => {
        const rootKey = getRootKey(root);

        if (!rootKey) {
            return;
        }

        const knownKeys = tracker.members.get(rootKey);

        if (!knownKeys) {
            tracker.members.set(rootKey, seenKeys);
            return;
        }

        // Mirror the list: an event that carries no legs is a partial update, so known legs stay.
        const carriesLegs = Array.isArray(root?.children) && root.children.length > 0;

        if (knownKeys.length > 1 && !carriesLegs) {
            const merged = knownKeys.slice();
            seenKeys.forEach(seenKey => {
                if (!merged.includes(seenKey)) {
                    merged.push(seenKey);
                }
            });
            tracker.members.set(rootKey, merged);
            return;
        }

        knownKeys.forEach(knownKey => {
            if (seenKeys.includes(knownKey)) {
                return;
            }

            const contribution = tracker.contributions.get(knownKey) ?? null;
            tracker.contributions.set(knownKey, null);

            if (contribution) {
                applyContribution(ensureDraft(), contribution, -1);
            }
        });

        tracker.members.set(rootKey, seenKeys);
    };

    const releaseUnseenActives = (seenKeys: Set<string>) => {
        seenRoots.forEach(rootKey => {
            (tracker.members.get(rootKey) || []).forEach(key => seenKeys.add(key));
        });

        tracker.contributions.forEach((contribution, key) => {
            if (tracker.ignored.has(key) || !contribution || contribution.bucket !== 'active' || seenKeys.has(key)) {
                return;
            }

            const closed: CallContribution = {group: contribution.group, bucket: 'unsuccess'};
            applyContribution(ensureDraft(), contribution, -1);
            applyContribution(ensureDraft(), closed, 1);
            tracker.contributions.set(key, closed);
        });
    };

    const applyItem = (wrapper: any): string | null => {
            const item = wrapper.item;
            const id = item?.id;

            if (id === null || id === undefined || id === '') {
                return null;
            }

            const key = String(id);
            const group = getCallGroup(item);
            const bucket = resolveBucket(item?.status, item?.end);
            const isKnown = tracker.contributions.has(key);
            const previous = tracker.contributions.get(key) ?? null;

            // An event without a readable status must not silently drop a call we already count.
            const next: CallContribution | null =
                group && passesAllFilters(wrapper, hubFilters)
                    ? (bucket ? {group, bucket} : previous)
                    : null;

            const startMs = toTimeMs(item?.start);
            const endMs = toTimeMs(item?.end);
            const startedAfterSnapshot = startMs !== null && startMs > tracker.boundaryMs;
            const endedBeforeSnapshot = endMs !== null && endMs <= tracker.boundaryMs;

            if (tracker.ignored.has(key)) {
                if (next && next.bucket !== 'active') {
                    tracker.ignored.delete(key);
                    tracker.contributions.set(key, next);
                    if (next) {
                        applyContribution(ensureDraft(), next, 1);
                    }
                } else {
                    tracker.contributions.set(key, next);
                }
                return key;
            }

            let effectivePrevious: CallContribution | null;
            if (isKnown) {
                effectivePrevious = previous;
            } else if (next?.bucket === 'active' && !startedAfterSnapshot) {
                const claimed = claimSnapshotContribution(
                    tracker,
                    next,
                    startedAfterSnapshot,
                    endedBeforeSnapshot
                );

                if (!claimed) {
                    tracker.ignored.add(key);
                    tracker.contributions.set(key, next);
                    return key;
                }

                effectivePrevious = claimed;
            } else {
                effectivePrevious = claimSnapshotContribution(
                    tracker,
                    next,
                    startedAfterSnapshot,
                    endedBeforeSnapshot
                );
            }

            tracker.contributions.set(key, next);

            if (sameContribution(effectivePrevious, next)) {
                return key;
            }

            if (effectivePrevious) {
                applyContribution(ensureDraft(), effectivePrevious, -1);
            }

            if (next) {
                applyContribution(ensureDraft(), next, 1);
            }

            return key;
    };

    batches.forEach(batch => {
        const rows = normalizeHubMonitoringRows(keysFirstLetterToLowerCase(batch));

        rows.forEach(root => {
            const rootKey = getRootKey(root);
            if (rootKey) {
                seenRoots.add(rootKey);
            }

            const rootSeenKeys: string[] = [];

            flattenHubRows([root]).forEach(wrapper => {
                const key = applyItem(wrapper);

                if (key) {
                    rootSeenKeys.push(key);
                    seenKeys.add(key);
                }
            });

            releaseMissingLegs(root, rootSeenKeys);
        });
    });

    releaseUnseenActives(seenKeys);

    if (draft) {
        reconcileDashboardTotals(draft);
    }

    return draft || dashboard;
}
